import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { sincronizarSePendente } from "../lib/memberkit";
import { BotaoTema, LogoCF } from "../componentes/ui";
import { FALE_CONOSCO } from "../componentes/Rodape";
import { IconeDoCard } from "../componentes/icones";

type Modo = "entrar" | "primeiro-acesso" | "esqueci";

// Uma tela, três modos. "Primeiro acesso" é o recadastro da Fase 3: as senhas do
// Bubble não são recuperáveis, então TODO aluno antigo passa por aqui uma vez.
// O texto pede o e-mail da compra porque é ele que o vínculo da 000034 usa.
//
// No desenho, "Entrar" é o caminho primário e o recadastro é um convite fixo
// abaixo do formulário — três abas disputando o clique no momento em que 58 mil
// alunos vão tropeçar na senha antiga é o oposto do que eles precisam.
export default function Entrar() {
  const { sessao } = useAuth();
  const [modo, setModo] = useState<Modo>("entrar");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  if (sessao) return <Navigate to="/" replace />;

  function trocarModo(novo: Modo) {
    setModo(novo);
    setErro(null);
    setAviso(null);
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setAviso(null);
    setOcupado(true);
    try {
      const emailLimpo = email.trim().toLowerCase();
      if (modo === "entrar") {
        const { error } = await supabase.auth.signInWithPassword({
          email: emailLimpo,
          password: senha,
        });
        if (!error) {
          // O login é a única hora em que a senha em texto existe do nosso lado
          // depois do recadastro — é aqui que a sincronização com o Memberkit
          // pendente se resolve. Sem await: não travar a entrada do aluno por
          // causa de 10 chamadas a um sistema terceiro.
          void sincronizarSePendente(senha);
        }
        if (error?.code === "email_not_confirmed") {
          // Este erro só existe quando a SENHA ESTÁ CERTA (o GoTrue valida a
          // senha antes da confirmação), então distinguir não abre enumeração —
          // e mandar quem só falta clicar no link para "senha incorreta" o
          // empurrava para redefinição e re-cadastro à toa (revisão de 11/08).
          setErro("Falta um passo: confirme o e-mail pelo link que enviamos. Procure na caixa de entrada (e no spam).");
        } else if (error) {
          // "Não existe" e "senha errada" continuam indistinguíveis de
          // propósito: a distinção entregaria a lista de quem é aluno.
          setErro("E-mail ou senha incorretos. Se é o seu primeiro acesso ao novo portal, use “Criar minha senha”.");
        }
        // Sem redirect manual: o onAuthStateChange atualiza a sessão e o
        // <Navigate> lá em cima faz o resto.
      } else if (modo === "primeiro-acesso") {
        const { data, error } = await supabase.auth.signUp({
          email: emailLimpo,
          password: senha,
          options: { emailRedirectTo: `${window.location.origin}/` },
        });
        if (error?.code === "over_email_send_rate_limit") {
          setErro("Enviamos um link há pouco. Espere alguns minutos e procure o e-mail anterior antes de pedir outro.");
        } else if (error) {
          setErro(error.code === "user_already_exists"
            ? "Este e-mail já tem conta no novo portal. Use “Entrar” — ou “Esqueci minha senha”."
            : "Não foi possível criar a conta. Confira o e-mail e tente de novo.");
        } else if (data.user?.identities?.length === 0) {
          // Com confirmação de e-mail ligada o GoTrue não devolve erro para
          // e-mail já cadastrado E confirmado: devolve 200 com o usuário
          // ofuscado (identities vazio), para não denunciar quem tem conta.
          // Sem esta checagem a tela dizia "enviamos um link" e nenhum e-mail
          // chegava (revisão de 11/08).
          setErro("Este e-mail já tem conta no novo portal. Use “Entrar” — ou “Esqueci minha senha”.");
        } else {
          // Detalhe que dói se faltar: se a pessoa já tinha feito o primeiro
          // acesso e NÃO confirmou, o GoTrue reenvia o link mas MANTÉM a senha
          // da primeira tentativa — a digitada agora é descartada.
          setAviso("Quase lá: enviamos um link de confirmação para o seu e-mail. O acesso é liberado depois do clique. "
            + "Se você já tinha tentado antes, vale a senha da primeira tentativa — não lembra? Use “Esqueci minha senha”.");
        }
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(emailLimpo, {
          redirectTo: `${window.location.origin}/redefinir-senha`,
        });
        if (error) {
          setErro("Não foi possível enviar o link. Tente de novo em instantes.");
        } else {
          // Mensagem igual exista ou não a conta, pelo mesmo motivo do login.
          setAviso("Se este e-mail tiver conta, o link de redefinição chega em instantes.");
        }
      }
    } finally {
      setOcupado(false);
    }
  }

  const titulo =
    modo === "entrar" ? "Entrar" : modo === "primeiro-acesso" ? "Criar minha senha" : "Esqueci minha senha";
  const subtitulo =
    modo === "entrar"
      ? "Use o e-mail da sua compra."
      : modo === "primeiro-acesso"
        ? "Só uma vez, e você já entra direto nas próximas."
        : "Enviamos um link para você criar uma senha nova.";
  const rotuloBotao =
    modo === "entrar" ? "Entrar" : modo === "primeiro-acesso" ? "Criar meu acesso" : "Enviar link";

  return (
    <div className="grid min-h-screen lg:grid-cols-[55fr_45fr]">
      {/* Painel da marca: azul CF com o grid quadriculado e os cards claros em
          escada — a combinação assinatura do design system. */}
      <div className="painel-navy flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="mb-10 flex items-center justify-between gap-4 sm:mb-14">
          <LogoCF className="h-8" />
          <BotaoTema />
        </div>
        <h1 className="rise max-w-xl text-3xl leading-tight font-bold text-white sm:text-4xl lg:text-[2.7rem]">
          A sua área do aluno,
          <br />
          <span className="text-laranja">tudo em um lugar só.</span>
        </h1>
        <p className="rise mt-4 text-navy-mute sm:text-lg" style={{ "--i": 1 } as React.CSSProperties}>
          Aulas, ferramentas e a sua faculdade. Conta com a gente!
        </p>

        <div className="mt-12 hidden max-w-md flex-col gap-3 lg:flex" aria-hidden="true">
          {[
            { slug: "aulas", titulo: "Aulas", texto: "Assista no seu ritmo, de onde parou" },
            { slug: "comunidade", titulo: "Comunidade", texto: "Professores e colegas na Área VIP" },
            { slug: "secretaria-do-aluno", titulo: "Faculdade FBC", texto: "Matrícula, secretaria e notas" },
          ].map((item, i) => (
            // Cores FIXAS neste bloco, e não tokens de tema: o fundo é o painel
            // navy, que é azul nos dois temas, e o card é branco por decisão de
            // composição. Com `text-tinta`/`text-cinza` o texto clareava no modo
            // escuro e ficava branco sobre branco — ilegível. Aqui o contexto
            // manda mais que o tema.
            <div
              key={item.slug}
              className="rise flex items-center gap-3.5 rounded-[0.875rem] bg-white p-3.5 shadow-[0_16px_36px_-18px_rgba(5,13,28,0.65)]"
              style={{ "--i": i + 2, marginLeft: `${i * 2.2}rem` } as React.CSSProperties}
            >
              <span className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-laranja/15 text-[#c76a00]">
                <IconeDoCard slug={item.slug} />
              </span>
              <span>
                <b className="block font-display text-sm font-semibold text-azul">{item.titulo}</b>
                <span className="text-xs text-azul/70">{item.texto}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Formulário */}
      <div className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <div className="surface p-7 sm:p-8">
            <h2 className="text-2xl font-bold text-titulo">{titulo}</h2>
            <p className="mt-1 text-sm text-cinza">{subtitulo}</p>

            {modo === "primeiro-acesso" && (
              <p className="mt-4 rounded-[0.875rem] border border-borda bg-tile p-3.5 text-sm text-tinta">
                Use o <strong>mesmo e-mail da sua compra</strong>: é ele que liga a conta nova aos seus
                cursos. A senha antiga não vale no novo portal.
              </p>
            )}

            <form onSubmit={enviar} className="mt-5 flex flex-col gap-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-tinta">E-mail</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seuemail@exemplo.com.br"
                  className="h-12 w-full rounded-xl border-[1.5px] border-borda bg-superficie px-4 text-tinta transition placeholder:text-cinza/60 focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none"
                />
              </label>

              {modo !== "esqueci" && (
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-tinta">
                    {modo === "primeiro-acesso" ? "Crie uma senha" : "Senha"}
                  </span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete={modo === "primeiro-acesso" ? "new-password" : "current-password"}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    className="h-12 w-full rounded-xl border-[1.5px] border-borda bg-superficie px-4 text-tinta transition focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none"
                  />
                  {modo === "primeiro-acesso" && (
                    <span className="mt-1.5 block text-sm text-cinza">Mínimo de 8 caracteres.</span>
                  )}
                </label>
              )}

              {/* Fora do <label>: dentro dele, clicar no botão também focava o
                  campo de senha, e o rótulo passava a "pertencer" a dois
                  controles. */}
              {modo === "entrar" && (
                <button
                  type="button"
                  onClick={() => trocarModo("esqueci")}
                  className="-mt-2 self-start py-1 text-sm font-medium text-link underline underline-offset-3"
                >
                  Esqueci minha senha
                </button>
              )}

              {erro && (
                <p role="alert" className="rounded-xl bg-erro-fundo p-3.5 text-sm leading-relaxed text-erro-tinta">
                  {erro}
                </p>
              )}
              {aviso && (
                <p role="status" className="rounded-xl bg-aviso-fundo p-3.5 text-sm leading-relaxed text-aviso-tinta">
                  {aviso}
                </p>
              )}

              <button type="submit" disabled={ocupado} className="pill-cta mt-1 w-full py-3.5 text-base">
                {ocupado ? "Aguarde…" : rotuloBotao}
              </button>
            </form>

            {/* O caminho de quem não recebe o e-mail (000379): o suporte libera, e o
                aluno cria a senha numa página própria, sem link. */}
            {modo !== "entrar" && (
              <p className="mt-4 text-sm leading-relaxed text-cinza">
                O e-mail não chega? Fale com o suporte. Se ele liberar a sua senha,{" "}
                <Link to="/senha-liberada" className="font-semibold text-link underline underline-offset-3">
                  crie a senha aqui
                </Link>
                .
              </p>
            )}

            {/* O convite ao recadastro fica fixo e visível: é o caminho de quem
                nunca entrou no portal novo, e a maioria dos alunos é assim. */}
            {modo === "entrar" ? (
              <div className="mt-5 rounded-[0.875rem] border border-borda bg-tile p-4">
                <p className="text-sm text-tinta">
                  <strong className="text-titulo">Primeira vez no novo portal?</strong> A sua senha antiga
                  não vale aqui. Crie a nova em um minuto, com o e-mail da compra.
                </p>
                <button onClick={() => trocarModo("primeiro-acesso")} className="btn-contorno mt-3 w-full">
                  Criar minha senha
                </button>
              </div>
            ) : (
              <button
                onClick={() => trocarModo("entrar")}
                className="mt-5 w-full text-sm font-semibold text-link underline underline-offset-3"
              >
                Voltar para o acesso com senha
              </button>
            )}
          </div>

          {/* Link de verdade, não frase solta: quem não consegue entrar precisa
              do canal AGORA, e o rodapé completo do hub só existe depois do
              login. */}
          <p className="mt-5 text-center text-sm text-cinza">
            Precisa de ajuda?{" "}
            <a
              href={FALE_CONOSCO}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-link underline underline-offset-4 transition hover:text-laranja-texto"
            >
              Fale com o nosso suporte
              <span className="sr-only"> (abre em outra aba)</span>
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
